/**
 * "3 explicit systems hidden by your settings", with the switch on the page itself. Shown under
 * a list's filters (and on search results) only when explicit records are in play: hidden ones
 * offer to show, shown ones offer to hide.
 *
 * Signed in, the switch is the account setting, so it sticks. Signed out, there is no setting to
 * change, so it adds `?explicit=1` to this page's address instead (the API honours it from anyone).
 */
import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { Eye, EyeOff } from 'lucide-react'
import { api } from '../lib/api'
import { plural } from '../lib/format'
import { invalidate } from '../lib/query'
import { useSession } from '../lib/session'
import type { ExplicitCount } from '../lib/types'

interface Props {
  explicit: ExplicitCount | undefined
  /** What the list holds, singular: "system", "book", "record". */
  noun: string
  /** Its plural, when adding an s is wrong ("series"). */
  nouns?: string
}

export function ExplicitNotice({ explicit, noun, nouns = `${noun}s` }: Props) {
  const session = useSession()
  const [params, setParams] = useSearchParams()
  const [busy, setBusy] = useState(false)
  if (!explicit || explicit.count === 0) return null
  const viaUrl = params.get('explicit') === '1'
  const user = session.user

  const setShown = async (show: boolean) => {
    // A page opened with ?explicit=1, or anyone signed out, switches by address.
    if (viaUrl || !user) {
      const next = new URLSearchParams(params)
      if (show) next.set('explicit', '1')
      else next.delete('explicit')
      next.delete('page')
      setParams(next, { replace: true })
      return
    }
    setBusy(true)
    try {
      await api('/api/v1/me', { method: 'PATCH', json: { show_explicit: show } })
      invalidate('/api/')
    } finally {
      setBusy(false)
    }
  }

  const count = plural(explicit.count, `explicit ${noun}`, `explicit ${nouns}`)
  return (
    <div className="notice explicit-notice" role="status">
      {explicit.hidden ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
      <span>
        {explicit.hidden
          ? `${count} hidden${user ? ' by your settings' : ''}.`
          : `Includes ${count}${viaUrl ? '' : ', shown by your settings'}.`}
      </span>
      <button
        type="button"
        className="button button-quiet button-small"
        disabled={busy}
        onClick={() => void setShown(explicit.hidden)}
      >
        {explicit.hidden ? 'Show them' : 'Hide them'}
      </button>
    </div>
  )
}
